import { AxiosPromise, AxiosResponse, AxiosRequestConfig } from 'axios';
import { apiService } from '@app/services';
import { history, store, localStore, sessionStore, expirationStorage, refreshTokenStorage, hideIntercom, tenantBetas } from '@app/helpers';
import { IUserData } from '@app/models';
import fileDownload from 'js-file-download';

interface HomeRealmHandler {
    (domain: string, realm: string, noCache: boolean): void;
}

class BasicService {
    domain = window.location.host;

    getShellConfig(): AxiosPromise {
        return apiService.get('/api/shell', {
            local: true
        })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getYubikeyLink(): AxiosPromise {
        return apiService.get('/api/profile/yubikey/link', {
            local: true
        })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getAppTemplateReference() {
        const _app = document.querySelector('body #app');
        return _app;
    }

    setLocale(locale: string) {
        // TODO: Redo this on actions, reducers and request axios
        // this.currentUser.cultureCode = locale;
        // _app.$.localeAjax.url = '/api/locale?locale=' + locale;
    }

    getLocale(): string | undefined {
        const locale = JSON.stringify(localStore.getItem('i19nextLng'));
        return locale;
    }

    setShellConfig(shellConfig: string) {
        const storage = this.getStorage();
        storage.setItem('shell-config', shellConfig);
    }

    getStoredShellConfig(): string | null {
        const storage = this.getStorage();
        return storage.getItem('shell-config');
    }

    setOrgFilter(orgId: string) {
        const storage = this.getStorage();
        storage.setItem('org_filter', orgId);
    }

    clearUserLocalStorageData() {
        const storage = this.getStorage();

        storage.removeItem('kaseya-user');
        storage.removeItem('shell-config');
        storage.removeItem(refreshTokenStorage);
        storage.removeItem(expirationStorage);
    }

    // odataEscape(val) {
    //     return encodeURIComponent(val.replaceAll('\'', '\'\'').replaceAll('#', '%23'));
    // }

    getHelpUrl() {
        return 'https://helpdesk.kaseya.com/hc/en-gb/categories/4402987794193';
    }

    redirectAfterSignin(): void {
        // if userdata has the onboard flag then we need to navigate to /onboard
        const currentUser: IUserData = store.getState().signIn.currentUser as IUserData;
        if (currentUser.onboarding) {
            this.redirect('/onboard');
            return;
        }

        const shellConfig: any = store.getState().basic.shellConfig;
        if (shellConfig.licenseDetail?.isExpired) {
            this.redirect('/license/billing');
            return;
        }

        let redirectPath: string = store.getState().launch.redirectAfterSignin;
        if (redirectPath !== '') {
            this.redirect(redirectPath);
        }
        else {
            this.redirect('/apps');
        }
    }

    redirect(link: string | undefined, openInNewTab: boolean = false): void {
        if (link !== undefined) {
            if (link === '/' || link.includes('/authorize') || link.includes('/trust') || link.includes('/onboard') || link.includes('/signout') || link.includes('/signin')) {
                hideIntercom(window);
            }

            if ((link && (link.startsWith('http://') || link.startsWith('https://'))) || openInNewTab) {
                let win = window.open(link, '_blank');

                if (win) {
                    win.focus();
                } else {
                    window.location.href = link;
                }
                return;
            }
            else {
                history.push(link);
            }
        }
    }

    getStorage() {
        let storage = localStore;

        if (localStore.getItem('ephemeral_session') === 'true') {
            storage = sessionStore;
        }

        return storage;
    }

    handleExceptions(): boolean {
        return store.getState().basic.handleExceptions;
    }

    // TODO: Remove beta check when it goes public, remove default org check when enabled for suborgs
    isDefaultOrgBeta() {
        return store.getState().basic.shellConfig.betas.indexOf(tenantBetas.PasswordServer) >= 0;
    }

    isEolBannerEnabled() {
        return store.getState().basic.shellConfig.betas.indexOf(tenantBetas.HideEolBanner) < 0;
    }

    orgFeaturePasswordServerEnabled() {
        return store.getState().basic.shellConfig.orgLicenseFeatures.indexOf('PasswordServer') >= 0;
    }

    getGlobalFeature(featureName: string) : boolean {
        return !!store.getState().basic.shellConfig.globalFeatures[featureName];
    }

    getQueryVariable(variable: string) {
        const query: string = window.location.search.substring(1);

        const vars: string[] = query.split('&');

        for (let i = 0; i < vars.length; i++) {
            let pair = vars[i].split('=');

            if (pair[0] === variable) { return pair[1]; }
        }
        return ('');
    }

    getFragmentVariable(variable: string) {
        const fragment: string = window.location.hash.substring(1);

        const vars: string[] = fragment.split('&');

        for (let i = 0; i < vars.length; i++) {
            let pair = vars[i].split('=');

            if (pair[0] === variable) { return pair[1]; }
        }
        return ('');
    }

    getFile(url: string, filename: string) {
        return apiService.get(url, {
            responseType: 'blob',
            local: true
        }).then(res => {
            fileDownload(res.data, filename);
            basicService.redirect('/app');
        }).catch((error: AxiosResponse) => Promise.reject(error));
    }

    getCurrentOrgFilter(): string {
        const localStorageOrganization = localStore.getItem('org_filter') ? localStore.getItem('org_filter') : '';

        if (localStorageOrganization) {
            return localStorageOrganization;
        }
        else {
            return '';
        }
    }

    getHomeRealm(handler: HomeRealmHandler) { 
        const currentUser = store.getState().signIn.currentUser;
        const shellConfig = store.getState().basic.shellConfig;

        if (!currentUser ||
            !currentUser.oid ||
            !shellConfig ||
            !shellConfig.organizations) {

            handler(this.domain, "", true);
            return;
        }

        let orgId = localStore.getItem('org_filter') || '';

        if (!orgId) {
            orgId = currentUser.oid;

            localStore.setItem('org_filter', orgId);
        }

        let foundOrg: any = {};

        shellConfig.organizations.forEach(function (org: any) {
            if (org.id === orgId) {
                foundOrg = org;
            }
        });

        handler(this.domain, foundOrg ? foundOrg.homeRealm : "", false);
    }
}

export const basicService = new BasicService();
